import { QueryClient } from "@tanstack/react-query";

import { FileInterface } from "../../../types/RecordInterfaces";
import { FileType } from "../../../components/Inputs/UppyFile";
import { FileSources } from "../../../types/FileInterfaces";
import { uploadImage } from "../../../utils/GeneralUtils";
import { Filter } from "../../../types/GlobalInterfaces";
import StrUtils from "../../../utils/StrUtils";

/**
 * Builds the topLevelFilters for the Growth Drive hook
 */
export function BuildGCDriveFilters({
  recordID,
  moduleAPIName,
  fieldAPIName,
  folderID,
  fileTypes,
  fileType,
  isPrivate,
  filesToFilter = [],
}: {
  recordID: string | undefined;
  moduleAPIName: string | undefined;
  fieldAPIName: string | undefined;
  folderID: string | undefined;
  fileTypes: string[] | undefined;
  fileType: FileType | undefined;
  isPrivate: boolean | undefined;
  filesToFilter: FileInterface[] | undefined;
}) {
  const filters: Filter[] = [];

  // Filters when viewing the files from a record. (Image Gallery).
  if (recordID && moduleAPIName && fieldAPIName) {
    filters.push(
      ...[
        {
          Group: false,
          Field: "Sources.SourceID",
          Method: "equals",
          Value: recordID,
        },
        {
          Group: false,
          Field: "Sources.SourceModule",
          Method: "equals",
          Value: moduleAPIName,
        },
        {
          Group: false,
          Field: "Sources.FieldAPIName",
          Method: "equals",
          Value: fieldAPIName,
        },
      ]
    );
  }

  // Folder Filters
  if (folderID) {
    // Need to be fetched only the files inside the folder.
    filters.push({
      Group: false,
      Field: "FolderID",
      Method: "equals",
      Value: folderID,
    });
  } else {
    // Only fetching the files/folders which are not inside another folder.
    filters.push({
      Group: false,
      Field: "ParentFolder",
      Method: "is-empty",
    });
  }

  // File Type Filters
  if (fileTypes) {
    // To filter files base on a custom File Types list.
    filters.push({
      Group: false,
      Field: "File.Type",
      Method: "contains",
      Value: fileTypes,
    });
  } else {
    // To filter files base with a default File Type element(s).
    switch (fileType) {
      case "Image":
        filters.push({
          Group: false,
          Field: "File.Type",
          Method: "contains",
          Value: "image/",
        });
        break;
      case "Video":
        filters.push({
          Group: false,
          Field: "File.Type",
          Method: "contains",
          Value: "video/",
        });
        break;
      case "Audio":
        filters.push({
          Group: false,
          Field: "File.Type",
          Method: "contains",
          Value: "audio/",
        });
        break;
      case "Text":
        filters.push({
          Group: false,
          Field: "File.Type",
          Method: "contains",
          Value: ["application/json", "text/plain"],
        });
        break;
      case "Data":
        filters.push({
          Group: false,
          Field: "File.Type",
          Method: "contains",
          Value: [
            "application/",
            "text/csv",
            "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
          ],
        });
        break;
      default:
        break;
    }
  }

  // To filter files base on the File Privacy. (Used in the Growth Drive Explorer).
  if (typeof isPrivate === "boolean") {
    filters.push({
      Group: false,
      Field: "Private",
      Method: "equals",
      Value: isPrivate,
    });
  }

  /** To filter files that are already used by the record. */
  if (filesToFilter?.length) {
    const fileIds = filesToFilter.map((f) => f.RecordID);
    filters.push({
      Group: false,
      Field: "_id",
      Method: "not-in",
      Value: fileIds,
    });
  }

  return [
    {
      Group: true,
      Filters: filters,
    },
  ];
}

/**
 * Custom file upload function, used for Camera and Video capture.
 */
export async function captureCamera({
  target,
  privateContainer,
  fileSources,
}: {
  target: EventTarget & HTMLInputElement;
  privateContainer: boolean;
  fileSources: FileSources[];
  onSuccess?: () => void;
}) {
  if (!target.files || !target.files.length) return;
  const file = target.files[0];
  const fileID = StrUtils.uuidv4();

  const data = await uploadImage(
    file,
    true,
    true,
    !privateContainer,
    fileSources
  );

  if (typeof data === "string") return undefined;

  const newFile = data.files[0];

  newFile.AltText = "Gallery File";
  newFile.id = fileID;

  return newFile;
}

/**
 *
 * Helper function which filters all the deleted files.
 *
 * If `recordID`, `moduleAPIName`, `fieldAPIName` are provided, then the sources will be filtered too,
 * if after the filter the Sources array is empty then the record will be removed from the query.
 */
export function onDeleteFilesQueryClient({
  queryClient,
  tenantID,
}: {
  queryClient: QueryClient;
  tenantID: string;
}) {
  const queries = queryClient
    .getQueryCache()
    .findAll({ queryKey: [tenantID, "records", "Files"] });

  queries.forEach((query) => {
    const queryKey = query.queryKey;
    queryClient.invalidateQueries({
      queryKey,
    });
  });
}
